import { useEffect, useState, useCallback, use } from 'react'
import { FaSearch, FaFilter, FaTable, FaCalendarAlt, FaCar, FaEdit, FaTrash } from 'react-icons/fa'
import { supabase } from '../../supabaseClient'
import { useNavigate } from 'react-router-dom'

// Helper para formatear fechas dd-mm-aaaa
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const [year, month, day] = dateString.split('-')
  return `${day}-${month}-${year}`
}


export default function ServiceSearch() {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [profile, setProfile] = useState(null)
  const navigate = useNavigate()

  const [patenteFiltro, setPatenteFiltro] = useState('')
  const [marcaFiltro, setMarcaFiltro] = useState('')
  const [fechaInicioFiltro, setFechaInicioFiltro] = useState('')
  const [fechaFinFiltro, setFechaFinFiltro] = useState('')

  // Cargar perfil del usuario logueado
  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError) { setError(userError.message); return }
      if (user) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id, role, lubricentro')
          .eq('id', user.id)
          .single()
        if (profileError) setError(profileError.message)
        else setProfile(profileData)
      }
    }
    fetchProfile()
  }, [])

  // Funciones para filtros
  const handlePatenteChange = (e) => setPatenteFiltro(e.target.value.toUpperCase())
  const handleMarcaChange = (e) => setMarcaFiltro(e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1))

  // Cargar servicios según filtros
  const fetchServices = useCallback(async () => {
    if (!profile) return
    setLoading(true)
    setError(null)

    let query = supabase.from('services').select('*').order('fecha_service', { ascending: false })

    // Filtro por lubricentro si es empleado
    if (profile.role === 'employee' && profile.lubricentro) query = query.eq('lubricentro', profile.lubricentro)
    if (patenteFiltro.trim() !== '') query = query.ilike('patente', `%${patenteFiltro.trim().toUpperCase()}%`)
    if (marcaFiltro.trim() !== '') query = query.ilike('marca', `%${marcaFiltro.trim()}%`)
    if (fechaInicioFiltro !== '' && fechaFinFiltro !== '') query = query.gte('fecha_service', fechaInicioFiltro).lte('fecha_service', fechaFinFiltro)
    else if (fechaInicioFiltro !== '') query = query.gte('fecha_service', fechaInicioFiltro)
    else if (fechaFinFiltro !== '') query = query.lte('fecha_service', fechaFinFiltro)

    const { data, error } = await query
    if (error) { setError(error.message); setServices([]) }
    else setServices(data)

    setLoading(false)
  }, [profile, patenteFiltro, marcaFiltro, fechaInicioFiltro, fechaFinFiltro])

  // Ejecutar búsqueda solo si hay filtros
  useEffect(() => {
    const hasFilters = patenteFiltro.trim() !== '' || marcaFiltro.trim() !== '' || fechaInicioFiltro !== '' || fechaFinFiltro !== ''
    if (hasFilters && profile) fetchServices()
    else { setServices([]); setLoading(false); setError(null) }
  }, [fetchServices, profile, patenteFiltro, marcaFiltro, fechaInicioFiltro, fechaFinFiltro])

  const clearFilters = () => {
    setPatenteFiltro('')
    setMarcaFiltro('')
    setFechaInicioFiltro('')
    setFechaFinFiltro('')
  }

  // Eliminar service con confirmación
  const handleDelete = async (service) => {
    if (!profile) return
    const canDelete = profile.role === 'admin' || (profile.role === 'employee' && profile.lubricentro === service.lubricentro)
    if (!canDelete) {
      alert('❌ No tienes permiso para eliminar este service.')
      return
    }

    if (!window.confirm(`¿Seguro que deseas eliminar el service de la patente ${service.patente}?`)) return

    const { error } = await supabase.from('services').delete().eq('id', service.id)
    if (error) {
      alert('❌ Error al eliminar: ' + error.message)
    } else {
      setServices((prev) => prev.filter((s) => s.id !== service.id))
    }
  }

  // Editar service → navegación con ID
  const handleEdit = (service) => {
    if (!profile) return
    const canEdit = profile.role === 'admin' || (profile.role === 'employee' && profile.lubricentro === service.lubricentro)
    if (!canEdit) {
      alert('❌ No tienes permiso para editar este service.')
      return
    }

    navigate(`/edit-service/${service.id}`, { state: { serviceToEdit: service } })
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-accent to-primary rounded-lg flex items-center justify-center">
          <FaSearch size={24} color="white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Historial de Services</h1>
          <p className="text-secondary">Consulta, filtra y gestiona los servicios realizados</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters-container">
        <div className="filters-title">
          <FaFilter className="text-accent" />
          Filtros de Búsqueda
        </div>
        
        <div className="filters-grid">
          <div className="input-group">
            <input
              type="text"
              value={patenteFiltro}
              onChange={handlePatenteChange}
              placeholder="Buscar patente"
              className="form-input"
            />
            <FaCar className="input-icon" />
          </div>
          
          <div className="input-group">
            <input
              type="text"
              value={marcaFiltro}
              onChange={handleMarcaChange}
              placeholder="Buscar marca"
              className="form-input"
            />
            <FaCar className="input-icon" />
          </div>
          
          <div className="input-group">
            <input
              type="date"
              value={fechaInicioFiltro}
              onChange={(e) => setFechaInicioFiltro(e.target.value)}
              placeholder="Fecha inicio"
              className="form-input"
            />
            <FaCalendarAlt className="input-icon" />
          </div>
          
          <div className="input-group">
            <input
              type="date"
              value={fechaFinFiltro}
              onChange={(e) => setFechaFinFiltro(e.target.value)}
              placeholder="Fecha fin"
              className="form-input"
            />
            <FaCalendarAlt className="input-icon" />
          </div>
        </div>
        
        <div className="flex gap-2 mt-4">
          <button onClick={clearFilters} className="btn btn-ghost">
            Limpiar Filtros
          </button>
          <button onClick={fetchServices} className="btn btn-primary" disabled={!profile}>
            <FaSearch /> Buscar
          </button>
        </div>
      </div>

      {/* Resultados */}
      <div className="table-container full-bleed">
        <div className="flex items-center justify-between p-4 border-b border-border-color">
          <div className="flex items-center gap-2">
            <FaTable className="text-accent" />
            <span className="font-semibold">Resultados ({services.length})</span>
          </div>
        </div>
        
        {loading && (
          <div className="loading p-8">Cargando historial...</div>
        )}
        
        {error && (
          <div className="error m-4">
            <FaSearch /> Error: {error}
          </div>
        )}
        
        {!loading && services.length === 0 && (
          <div className="info m-4">
            <FaSearch />
            {patenteFiltro.trim() !== '' || marcaFiltro.trim() !== '' || fechaInicioFiltro !== '' || fechaFinFiltro !== '' 
              ? 'No se encontraron resultados con los filtros aplicados.'
              : 'Usa los filtros de arriba para buscar servicios específicos.'
            }
          </div>
        )}

        {!loading && services.length > 0 && (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Patente</th>
                  <th>Marca</th>
                  <th>Modelo</th>
                  <th>KM</th>
                  <th>Lubricante</th>
                  <th>Aceite</th>
                  <th>Filtro aceite</th>
                  <th>Filtro aire</th>
                  <th>Filtro comb.</th>
                  <th>Filtro hab.</th>
                  <th>Próx. service</th>
                  <th>Observaciones</th>
                  <th>Cliente</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {services.map((s) => (
                  <tr key={s.id} className="hover:bg-card-hover transition-colors">
                    <td>{formatDate(s.fecha_service)}</td>
                    <td className="font-mono font-semibold">{s.patente}</td>
                    <td>{s.marca}</td>
                    <td>{s.modelo}</td>
                    <td className="font-mono text-center">{s.km?.toLocaleString()}</td>
                    <td>{s.lubricante}</td>
                    <td className="text-center">{s.aceite ? '✓' : '✕'}</td>
                    <td className="text-center">{s.filtro_aceite ? '✓' : '✕'}</td>
                    <td className="text-center">{s.filtro_aire ? '✓' : '✕'}</td>
                    <td className="text-center">{s.filtro_combustible ? '✓' : '✕'}</td>
                    <td className="text-center">{s.filtro_habitaculo ? '✓' : '✕'}</td>
                    <td className="font-mono">{s.proximo_service ? `${s.proximo_service.toLocaleString()} km` : '-'}</td>
                    <td className="max-w-xs">
                      {s.observaciones && s.observaciones.trim() !== '' ? (
                        <ul className="space-y-1 text-sm text-white">
                          {s.observaciones.split('\n')
                            .map(line => line.trim())
                            .filter(line => line.length > 0)
                            .map((line, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <span className="w-1.5 h-1.5 mt-2 rounded-full bg-accent"></span>
                                <span className="leading-snug">{line}</span>
                              </li>
                            ))}
                        </ul>
                      ) : ('-')}
                    </td>
                    <td className="text-sm">
                      <div className="flex flex-col">
                        <span className="font-semibold">
                          {s.cliente_nombre || '-'}
                        </span>
                        <span className="text-secondary text-xs">
                          {s.cliente_telefono || ''}
                        </span>
                      </div>
                    </td>
                    <td className="flex items-center gap-3 justify-center">
                      <button onClick={() => handleEdit(s)} className="text-blue-400 hover:text-blue-600">
                        <FaEdit />
                      </button>
                      <button onClick={() => handleDelete(s)} className="text-red-400 hover:text-red-600">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
